import type { CSSObject } from '@emotion/react'
import deepmerge from 'deepmerge'
import { PartialDeep } from 'type-fest'
import {
  Color,
  FontWeight,
  Size,
  Spacing,
  Theme,
  Typography,
} from '@opallabs/gem'

export const classNamePrefix = 'lapidary-textArea--'
export const selectors = {
  autoResizeContainer: `${classNamePrefix}autoResizeContainer`,
  autoResizeInput: `${classNamePrefix}autoResizeInput`,
} as const

type MakeThemedOverrides<P = unknown> = Record<Theme, P>

type AutoExpandTextAreaTheme = MakeThemedOverrides<{
  active: {
    borderColor: string
  }
  backgroundColor: string
  borderColor: string
  horizontalPadding: number | string
  textColor: string
  verticalPadding: number | string
}>

export type AutoExpandTextAreaThemeOverrides =
  PartialDeep<AutoExpandTextAreaTheme>

const defaultTheme: AutoExpandTextAreaTheme = {
  dark: {
    active: {
      borderColor: Color.dark.Surface.A200,
    },
    backgroundColor: Color.dark.Surface.N100,
    borderColor: Color.dark.Surface.N300,
    horizontalPadding: Spacing.S,
    textColor: Color.dark.Text.Default,
    verticalPadding: Spacing.S,
  },
  light: {
    active: {
      borderColor: Color.light.Surface.A200,
    },
    backgroundColor: Color.light.Surface.N100,
    borderColor: Color.light.Surface.N300,
    horizontalPadding: Spacing.S,
    textColor: Color.light.Text.Default,
    verticalPadding: Spacing.S,
  },
}

const CHAR_COUNT_PADDING = 42

export const textArea = ({
  theme: colorTheme,
  themeOverrides,
  truncate,
  showCounterOnlyOnFocus = false,
  maxLength,
  maxRows,
}: {
  theme: Theme
  themeOverrides?: AutoExpandTextAreaThemeOverrides
  truncate?: boolean
  showCounterOnlyOnFocus?: boolean
  maxLength?: number | null
  maxRows?: number
}): CSSObject => {
  const componentTheme =
    themeOverrides !== undefined
      ? deepmerge(defaultTheme[colorTheme], themeOverrides[colorTheme] || {})
      : defaultTheme[colorTheme]

  const lineClamp = (maxRows: number): CSSObject => ({
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: maxRows,
  })

  return {
    ...Typography.T500,
    backgroundColor: componentTheme.backgroundColor,
    border: `1px solid ${componentTheme.borderColor}`,
    color: componentTheme.textColor,
    borderRadius: Size.Rounding,
    boxSizing: 'border-box',
    paddingBlock: componentTheme.verticalPadding,
    paddingInline: componentTheme.horizontalPadding,
    resize: 'none',
    outline: 'none',
    paddingInlineEnd: maxLength
      ? CHAR_COUNT_PADDING
      : componentTheme.horizontalPadding,

    '&:active:enabled, &:focus:enabled': {
      border: `1px solid ${componentTheme.active.borderColor}`,
    },
    '&:focus:enabled': {
      backgroundColor: Color[colorTheme]['Color/Surface/Surface-1'],
    },
    '&:hover:enabled': {
      backgroundColor: Color[colorTheme].Translucent.A08,
    },

    '&:disabled': {
      opacity: 0.5,
    },

    '&:not(:focus) + span': showCounterOnlyOnFocus ? { display: 'none' } : {},
    '&:not(:focus)': truncate && maxRows ? lineClamp(maxRows) : {},
  }
}

export const error = (colorTheme: Theme): CSSObject => ({
  outline: 'none',
  borderColor: Color[colorTheme].Surface.R100,

  '&:active:enabled, &:focus:enabled': {
    borderColor: Color[colorTheme].Surface.R100,
    boxShadow: `0 0 0 1px ${Color[colorTheme].Surface.A200}`,
  },
})

export const characterCounter = (colorTheme: Theme): CSSObject => ({
  ...Typography.T300,
  fontWeight: FontWeight.Medium,
  position: 'absolute',
  insetBlockStart: Spacing.M * 0.625,
  insetInlineEnd: Spacing.M,
  color: Color[colorTheme].Text.Gray,
})
